import type { FC } from 'react';
import Icon from '@components/Icon';
import { Show } from '@components/Show';
import { DragOverlay } from '@dnd-kit/core';
import type { ResourceBlock } from '@domain/schemas/contract.schema';
import { ENDORSEMENT_DETAILS_SENTINEL } from '@domain/schemas/endorsement-summary.schema';
import { getBlockDisplayName } from '@root/helpers/blocks';

import { useNavigation } from '../context/use-navigation';
import { findBlockInSection } from '../hooks/drag-handlers.utils';
import type { NavItem } from '../types';

const overlayIconClasses = 'text-info-500 inline-flex h-6 w-6 items-center rounded-sm p-1';

export const NavigationDragOverlay: FC = () => {
  const { navItems, sections, activeId, activeType } = useNavigation();

  const getActiveItem = (): { section?: NavItem; block?: ResourceBlock } | null => {
    if (!activeId) return null;

    if (activeType === 'section') {
      const section = navItems.find((s) => s.id === activeId);
      return section ? { section } : null;
    }

    if (activeType === 'block') {
      for (const section of sections) {
        const block = findBlockInSection(section, activeId);
        if (block) return { block };
      }
    }

    return null;
  };

  const activeItem = getActiveItem();

  return (
    <DragOverlay>
      <Show when={activeItem?.section}>
        {(section) => (
          <div className="bg-info-50 mx-auto w-full max-w-xs rounded-sm px-2 py-2 text-sm shadow-lg">
            <div className="flex w-full place-items-center gap-x-2">
              <Icon
                name={section.id === ENDORSEMENT_DETAILS_SENTINEL ? 'material-changes' : 'layout'}
                className={overlayIconClasses}
              />
              <span className="font-medium">{section.label}</span>
            </div>
          </div>
        )}
      </Show>
      <Show when={activeItem?.block}>
        {(block) => (
          <div className="bg-info-50 max-w-xs cursor-grabbing rounded-sm p-1 py-1 pl-4 text-sm shadow-lg">
            <div className="flex w-full flex-nowrap place-items-center gap-2 truncate">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="shrink-0"
              >
                <rect width="16" height="16" rx="4" fill="#6B7280" />
                <path
                  d="M7.99935 4.5H12.666M7.99935 8H12.666M3.04102 4.5H5.37435M7.99935 11.5H12.666"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="truncate font-bold">{getBlockDisplayName(block)}</p>
            </div>
          </div>
        )}
      </Show>
    </DragOverlay>
  );
};
